import { Stack, Typography } from "@mui/material";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { CandidateShippingDocumentsForShipmentQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";

import { CANDIDATE_SHIPPING_DOCUMENTS_QUERY } from "./api";
import { formatShipmentDate } from "./shipmentDisplay";

export type CandidateShippingDocument = NonNullable<
  CandidateShippingDocumentsForShipmentQuery["tenant"]
>["wasteShippingDocuments"]["items"][number];

/** Everything that tells two documents apart when their numbers do not. */
function documentDetail(document: CandidateShippingDocument): string {
  return [
    `Shipped ${formatShipmentDate(document.shipDate)}`,
    document.transporterName,
    document.destinationName,
    document.facility?.name,
    `${document.lineCount} ${document.lineCount === 1 ? "line" : "lines"}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

function documentLabel(document: CandidateShippingDocument): string {
  return document.documentNumber ?? "No document number";
}

/**
 * The bills of lading (and other non-manifest shipping documents) a tenant has
 * on file, as a single autocomplete.
 *
 * An autocomplete rather than a search box above a selectable list. The list's
 * selected row was a faint background tint with no way to clear it, and typing
 * only narrowed the rows rather than moving toward an answer. Here the choice
 * lands in the input, the clear button undoes it, and the reader types a few
 * characters and picks — one control doing what two were doing badly.
 *
 * Options still carry ship date, transporter, destination, facility and line
 * count, for the same reason the list did: two documents from the same
 * transporter in the same week look identical by document number alone.
 */
export function ShippingDocumentPicker({
  facilityId,
  value,
  onChange,
}: {
  /**
   * Narrows the pool the way `AttachManifestDialog` does. A document from
   * another facility is refused by `billOfLadingNotAttachableViolation`, so
   * offering one would be a dead end.
   */
  facilityId: string;
  value: CandidateShippingDocument | null;
  onChange: (document: CandidateShippingDocument | null) => void;
}) {
  const { tenantId } = useTenant();

  return (
    <AsyncAutocomplete
      query={CANDIDATE_SHIPPING_DOCUMENTS_QUERY}
      variables={{ tenantId: tenantId ?? "" }}
      additionalSearchString={`facilityId:${facilityId}`}
      getItems={(data) => data.tenant?.wasteShippingDocuments?.items ?? []}
      getOptionLabel={documentLabel}
      getOptionKey={(document) => document.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={value}
      onChange={onChange}
      // Newest first: a document is usually attached within days of the ship
      // date, so the one being looked for is near the top before any typing.
      initialSortModel={[{ field: "shipDate", sort: "desc" }]}
      lazyLoad
      fullWidth
      placeholderLabel="Search by document number"
      renderOption={(props, document) => (
        <li {...props} key={document.id}>
          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap>
              {documentLabel(document)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {documentDetail(document)}
            </Typography>
          </Stack>
        </li>
      )}
      noOptionsText="No shipping documents found. A document appears here once it has been saved."
    />
  );
}
